/**
 * Act 5 — T3 reverse-corridor scenario. 18 screens: Pampagro RFQ → Gulf
 * Phosphate reply → discovery → FOB picker (Pampagro arranges) →
 * contract capture → doc carousel (fertilizer set) → reconciliation →
 * invoice → compliance → tariff estimate → LC composer → gate cascade →
 * LC issued → smart logistics → insurance cert → tariff final →
 * settlement → T3 reveal.
 *
 * Shipment (5.14 SmartLogistics) + insurance-cert anchor (5.15) sit
 * AFTER LC issuance (5.13) so the narrative matches real bank-LC
 * trade-finance convention — seller ships once the LC is in hand.
 * Mirrors T2's act 4 layout exactly.
 */

import type { ReactElement } from "react";
import type { T3Screen } from "@/lib/scenes/screens";
import type { SceneRenderContext } from "@/lib/scenes/act4";

import { PampagroRfq, GulfPhosphateReply } from "@/components/scenes/act5/PampagroRfq";
import { ReverseCorridorDiscovery } from "@/components/scenes/act5/ReverseCorridorDiscovery";
import { LogisticsInsurancePicker } from "@/components/scenes/shared/LogisticsInsurancePicker";
import { ContractCaptureWithHandshake } from "@/components/scene/ContractCaptureWithHandshake";
import { DocumentCarousel, FERTILIZER_DOCS } from "@/components/scenes/act2/DocumentCarousel";
import { ReconciliationMatrix }    from "@/components/scenes/act2/ReconciliationMatrix";
import { InvoiceFsmTile }          from "@/components/scenes/act2/InvoiceFsmTile";
import { SmartLogistics }          from "@/components/scenes/act2/SmartLogistics";
import { InsuranceCertAnchor }     from "@/components/scenes/shared/InsuranceCertAnchor";
import { ComplianceSnapshot }      from "@/components/scenes/act2/ComplianceSnapshot";
import { TariffEstimateTile, TariffFinalTile } from "@/components/scenes/shared/TariffTiles";
import { LcComposer }              from "@/components/scenes/act3/LcComposer";
import { GateCascade }             from "@/components/scenes/act3/GateCascade";
import { LcIssued }                from "@/components/scenes/act3/LcIssued";
import { SettlementSimulation }    from "@/components/scenes/act3/SettlementSimulation";
import { T3Reveal }                from "@/components/scenes/act5/T3Reveal";
import { InvestorEvidence }        from "@/components/scenes/shared/InvestorEvidence";
import { RegulatorEvidence }       from "@/components/scenes/shared/RegulatorEvidence";
import { BankerEvidence }          from "@/components/scenes/shared/BankerEvidence";
import { CraneIllustration }       from "@/components/scenes/shared/illustrations/CraneIllustration";
import { LcCompositionProgress }   from "@/components/scenes/shared/LcCompositionProgress";

import { pickInvoiceState, lcGateDecisions, scenarioReputationT3 } from "@/components/scene/SceneRouterHelpers";
import { LcPlacementFeeCta } from "@/components/scenes/act3/LcPlacementFeeCta";
import { USER_ACTION } from "@/lib/userActions/UserActionsContext";

export const ACT5_RENDERERS: Record<T3Screen, (ctx: SceneRenderContext) => ReactElement> = {
  "5.1":  () => <PampagroRfq />,
  "5.2":  () => <GulfPhosphateReply />,
  "5.3":  () => <ReverseCorridorDiscovery />,
  "5.4":  () => (
    <LogisticsInsurancePicker
      incoterms="FOB"
      arrangedBy="Pampagro"
      lossPayee="Itaú"
      cifBasisUsd={3_400_000}
      interactiveInsurerPick
      hideCompositionBlock
      insuranceBadgeStyle="mandatory"
      carriers={[
        { name: "Hapag-Lloyd · Bulk", modality: "Bulk-carrier", etaDays: 24, onTimePct: 89, selected: true },
        { name: "MSC · Bulk",         modality: "Bulk-carrier", etaDays: 26, onTimePct: 86, selected: false },
        { name: "K Line · Bulk",      modality: "Bulk-carrier", etaDays: 25, onTimePct: 88, selected: false },
      ]}
      insurers={[
        { name: "Bradesco Seguros", policyTemplate: "Marine Cargo · Institute Clauses (A)", selected: true },
        { name: "Porto Seguro",     policyTemplate: "Marine Cargo · Institute Clauses (A)", selected: false },
        { name: "AXA XL Brasil",    policyTemplate: "Marine Cargo · Institute Clauses (B)", selected: false },
      ]}
    />
  ),
  "5.5": ({ ev, signParty }) => (
    <ContractCaptureWithHandshake
      ev={ev}
      signParty={signParty}
      requireSignCta
      showLcCompositionTrigger
      signCtaActionKey={USER_ACTION.T3_SIGN_CONTRACT}
      lcCompositionActionKey={USER_ACTION.T3_BEGIN_LC_COMPOSITION}
    />
  ),
  "5.6": ({ ev, graph }) => (
    <DocumentCarousel
      documents={ev.documents}
      graph={graph}
      docs={FERTILIZER_DOCS}
      carouselSubtitle="Document carousel · 6 fertilizer-specific tiles"
      headerAttestor={{
        src: "/attestors/orbid-fed-uae-chambers.svg",
        alt: "FUC",
        caption: "Origination attested · Federation of UAE Chambers (FUC)",
      }}
    />
  ),
  "5.7": ({ ev }) => <ReconciliationMatrix reconciliation={ev.reconciliation} />,
  "5.8": ({ ev, signParty, invoiceSignerHeader }) => (
    <InvoiceFsmTile
      invoiceState={pickInvoiceState(ev, "INV-2026-0342")}
      invoiceId="INV-2026-0342"
      einvoiceSigner={signParty.einvoice}
      einvoicePdf={signParty.einvoicePdf}
      invoiceSignerHeader={invoiceSignerHeader}
      requireSignCta
      ctaActionKey={USER_ACTION.T3_SIGN_INVOICE}
    />
  ),
  "5.9": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <ComplianceSnapshot
          snapshot={ev.complianceSnapshot}
          expectedCorridorLabel="Corridor AE-BR · UAE (origin) → Brazil (destination)"
          expectedAuthorities={["Receita Federal (BRL)", "Siscomex", "MAPA — Import Mode", "UAE Federal Authority for Customs", "ATLP AD Ports", "OFAC + UAE Sanctions (Internal Use Only)", "Compliance Hard Stops (Internal Use Only)"]}
        />
      </div>
      <InvestorEvidence variant="trade-body" notionalUsd={3_400_000} stageLabel="Compliance gate" />
    </div>
  ),
  "5.10": () => (
    <div className="flex h-full flex-col gap-3">
      <TariffEstimateTile cifUsd={3_400_000} jurisdiction="BR" hsCode="3102.10" />
      <LcCompositionProgress
        startPct={15}
        targetPct={50}
        label="Integrating estimated Tariff into LC composition"
      />
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-orbitra-muted">
        Tariff estimate at LC composition · BR import
      </p>
      <div className="flex-1 min-h-0" />
    </div>
  ),
  "5.11": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <LcCompositionProgress
        startPct={50}
        targetPct={85}
        label="Integrating contract and invoice data into LC composition"
      />
      <div className="flex-1 min-h-0">
        <LcComposer contract={ev.contract} composer={ev.lcComposer} lcMode tenorAsPackage selectedTenorDays={90} />
      </div>
      <BankerEvidence variant="lc-composer" amountUsd={3_400_000} bankBadge="Itau" />
    </div>
  ),
  "5.12": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <GateCascade decisions={lcGateDecisions(ev, "t3-reverse-corridor")} settlementRef="LC-2026-00342" />
      </div>
      <BankerEvidence variant="gate-cascade" amountUsd={3_400_000} bankBadge="Itau" />
      <LcPlacementFeeCta
        feeUsd={850}
        bankBadge="Itaú"
        actionKey={USER_ACTION.T3_PAY_LC_PLACEMENT_FEE}
      />
    </div>
  ),
  "5.13": ({ ev, graph }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <LcIssued
          lc={ev.lcIssuance}
          buyerLabel="Pampagro · Importer"
          sellerLabel="Gulf Phosphate · Exporter"
          graph={graph}
          showDigitalLcModal
          digitalLcAutoOpenKey={USER_ACTION.T3_VIEW_LC_ISSUED}
        />
      </div>
      <RegulatorEvidence variant="lc-issued" lcReference={ev.lcIssuance?.lcReference} />
      <InvestorEvidence variant="trade-body" notionalUsd={3_400_000} stageLabel="LC issued" />
    </div>
  ),
  "5.14": ({ ev }) => {
    const loaded = ev.logisticsMilestones.some((m) => m.milestone === "Loading");
    return (
      <SmartLogistics
        milestones={ev.logisticsMilestones}
        direction="ae-to-br"
        rightColumnExtra={<CraneIllustration loaded={loaded} quayLabel="Abu Dhabi Port quay 4" />}
      />
    );
  },
  "5.15": ({ ev }) => (
    <InsuranceCertAnchor
      shipmentLoaded={ev.logisticsMilestones.some((m) => m.milestone === "Loading")}
      policyType="Marine cargo · Institute Clauses (A)"
      coverage="≥ 110% invoice · USD 3,740,000"
      insurerName="Bradesco Seguros"
      lossPayee="Itaú"
      arrangedBy="Pampagro"
      mandatoryUcp28={false}
      viewCertificatePdf="/sign-docs/insurance.pdf"
      viewCertificateRef="POL-2026-0342 · 110% invoice · ICC(A)"
    />
  ),
  "5.16": () => (
    <div className="grid h-full place-items-center">
      <div className="w-full max-w-2xl">
        <TariffFinalTile cifUsd={3_400_000} jurisdiction="BR" hsCode="3102.10" settled />
      </div>
    </div>
  ),
  "5.17": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <SettlementSimulation
          legs={ev.settlementLegs}
          threeFieldMatched={ev.threeFieldMatched}
          invoiceMatched={ev.invoiceState["INV-2026-0342"] === "MATCHED"}
        />
      </div>
      <BankerEvidence variant="settlement" amountUsd={3_400_000} bankBadge="Itau" />
      <RegulatorEvidence variant="settlement" />
      <InvestorEvidence variant="trade-body" notionalUsd={3_400_000} stageLabel="Settlement" />
    </div>
  ),
  "5.18": ({ ev }) => {
    const t3 = scenarioReputationT3(ev);
    return (
      <div className="flex h-full flex-col gap-3">
        <div className="flex-1 min-h-0">
          <T3Reveal pampagro={t3.pampagro} gulfPhosphate={t3.gulfPhosphate} />
        </div>
        <InvestorEvidence variant="reveal" notionalUsd={3_400_000} />
      </div>
    );
  },
};
